import styled from "@emotion/styled";
import { getProfileData } from "@src/api/profile";
import { ExpertProfileForm, GetProfileParameter } from "@src/api/types";
import useLoader from "@src/hook/useLoader";
import { ButtonA, GroupA } from "@src/ruix";
import { globalSnackbarState } from "@src/state";
import { useRouter } from "next/router";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useSetRecoilState } from "recoil";
import { EmptySpace } from "../SignComponent";
import { Section1 } from "./other/Section1";
import { Section2 } from "./other/Section2";
import { Section3 } from "./other/Section3";

interface OtherProfileProps {
	matchUuid: string;
	expertUuid: string;
	matchedExpertDataUuid: string;
	isNoShowButton?: boolean;
	om?: string;
	direct?: string;
	direct2?: string;
	omCallback?: () => void;
	isRecommended?: boolean;
	aiSummarySlot?: ReactNode;
}

export const OtherProfile = ({
	matchUuid,
	expertUuid,
	matchedExpertDataUuid,
	isNoShowButton,
	om,
	direct,
	direct2,
	isRecommended,
	omCallback,
	aiSummarySlot,
}: OtherProfileProps) => {
	const router = useRouter();
	const withLoader = useLoader();
	const [expertInfo, setExpertInfo] = useState<ExpertProfileForm>();
	const setSnackbareState = useSetRecoilState(globalSnackbarState);
	const profileQueryParameter = ["getProfile", expertUuid, matchUuid];
	const profileParameter: GetProfileParameter = {
		type: "other",
		expertUuid: expertUuid,
		matchUuid: matchUuid,
	};

	const getOverlappingStrings = (str1: string, str2: string): string[] => {
		const arr1 = str1
			.split(",")
			.map((s) => s.trim())
			.filter((s) => s !== "");
		const arr2 = str2
			.split(",")
			.map((s) => s.trim())
			.filter((s) => s !== "");

		return arr1.filter((value) => arr2.includes(value));
	};

	const matchedSkills = useMemo(() => {
		if (!expertInfo) return [];
		const expertSkills = expertInfo?.industry + "," + expertInfo?.skill;
		const neededSkills = expertInfo?.matchNeededSkills || "";

		return getOverlappingStrings(expertSkills, neededSkills);
	}, [expertInfo]);

	const { isLoading, isError, data } = useQuery(
		profileQueryParameter,
		async () =>
			await withLoader(
				getProfileData(profileParameter),
				"로딩 중입니다\n1~3초 정도 소요됩니다",
			),
		{
			refetchOnWindowFocus: false, // react-query는 사용자가 사용하는 윈도우가 다른 곳을 갔다가 다시 화면으로 돌아오면 이 함수를 재실행합니다. 그 재실행 여부 옵션 입니다.
			refetchOnMount: false,
			retryOnMount: false,
			staleTime: Infinity,
			cacheTime: Infinity,
			onError: (error) => {
				setSnackbareState(
					"전문가 데이터가 존재하지 않습니다.\n이전 전문가라면 해당 아이디로 가입하셨는지 확인해주세요.",
				);
				router.replace("/expert-application");
			},
		},
	);

	useEffect(() => {
		if (data) {
			setExpertInfo(data.data);
		}
	}, [data]);

	if (isLoading || isError) {
		return null;
	}

	return (
		<Wrapper>
			{expertInfo && (
				<>
					<Content className="profile-inner-content">
						<Section1
							data={expertInfo}
							showOpenPrice={!!direct || !!direct2}
							isRecommended={isRecommended}
							aiSummarySlot={aiSummarySlot}
						/>
						<EmptySpace space={40} />
						<Section2
							data={expertInfo}
							matchUuid={matchUuid}
							matchedSkills={matchedSkills}
							matchedExpertDataUuid={matchedExpertDataUuid}
							isNoShowButton={isNoShowButton}
							noShowPortfolio={!!direct || !!direct2}
						/>
						{direct ? (
							<>
								<div
									style={{
										width: "100%",
										position: "fixed",
										bottom: "100px",
										left: "50%",
										transform: "translateX(-50%)",
									}}
									id={"accept-button"}
								>
									<GroupA order="row-center-center">
										<ButtonA
											fullWidth
											sx={{
												width: "500px",
												maxWidth: "90%",
											}}
											buttonClass="primary"
											size="large"
											onClick={async () => {
												window.open(
													`https://tally.so/r/mVvQ9g?eid=${expertUuid}`,
												);
											}}
										>
											업무 제안하기
										</ButtonA>
									</GroupA>
								</div>
							</>
						) : null}
						{direct2 ? (
							<>
								<div
									style={{
										width: "100%",
										position: "fixed",
										bottom: "100px",
										left: "50%",
										transform: "translateX(-50%)",
									}}
									id={"accept-button"}
								>
									<GroupA order="row-center-center">
										<ButtonA
											fullWidth
											sx={{
												width: "500px",
												maxWidth: "90%",
											}}
											buttonClass="primary"
											size="large"
											onClick={async () => {
												window.open(
													`https://tally.so/r/woKeKe?eid=${expertUuid}`,
												);
											}}
										>
											업무 제안하기
										</ButtonA>
									</GroupA>
								</div>
							</>
						) : null}
					</Content>
					{matchUuid && <Section3 />}
				</>
			)}
		</Wrapper>
	);
};

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	overflow: auto;
	display: flex;
	flex-direction: column;
	align-items: center;
	height: fit-content;

	* {
		white-space: pre-wrap;
		word-break: break-all;
	}
`;

const Content = styled.div`
	max-width: 1000px;
	width: 100%;
	background-color: #fff;
	border-radius: 30px 30px 0 0;
	padding: 30px;
	display: flex;
	flex-direction: column;
	justify-content: center;

	@media screen and (max-width: 768px) {
		padding: 10px;
		width: 100%;
		border-radius: 10px 10px 0 0;
	}
`;
